/**
 * 長い文章から検索語に一致した箇所の前後だけを抜き出し、一致した部分に印を付ける断片へ分けるユーティリティ
 * 一致の判定は keywordHighlight.js の findKeywordRanges（DB の LIKE と同じ照合）に任せる。
 * 描画する側は断片を補間で並べる（v-html を使わない）ので、ここでは HTML エスケープも印のタグ付けもしない。
 */
import { findKeywordRanges } from '@/lib/keywordHighlight';

/** 最初の一致の前後に出す文字数（UTF-16 の単位） */
export const DEFAULT_EXCERPT_RADIUS = 30;

const isLowSurrogate = (code) => code >= 0xDC00 && code <= 0xDFFF;

// 改行・連続する空白は 1 行で見せるため空白 1 つに畳む
const oneLine = (str) => str.replace(/\s+/g, ' ');

/**
 * 最初に一致した箇所を軸に前後 radius 文字を切り出し、範囲内の一致をすべて印の対象にした断片を返す。
 *   - 照合で文字数が変わる組（半角の濁点など）があるので、窓の端は検索語の長さでなく一致した範囲の終わりから数える
 *   - 窓の終わりにかかる一致は窓を広げて丸ごと入れる（語が途中までしか光らないのを避ける）
 *   - 窓の端がサロゲートペアの間に来たら外側へずらす（字を割らない）
 *
 * @param {string|null|undefined} text 記入内容
 * @param {string|number|null|undefined} keyword 検索語
 * @param {{ radius?: number }} [options] radius は 0 以上の整数（画面からは既定値のまま呼ぶ）
 * @returns {Array<{ text: string, highlight: boolean }>|null} 一致が無ければ null
 */
export function buildKeywordExcerpt(text, keyword, { radius = DEFAULT_EXCERPT_RADIUS } = {}) {
  if (typeof text !== 'string' || !text) return null;
  const ranges = findKeywordRanges(text, keyword);
  if (!ranges.length) return null;

  let start = Math.max(0, ranges[0].start - radius);
  let end = Math.min(text.length, ranges[0].end + radius);
  // 範囲は昇順で重ならないので、広がるのは窓の終わりにかかる 1 つだけ
  ranges.forEach((range) => {
    if (range.start < end) end = Math.max(end, range.end);
  });
  if (start > 0 && isLowSurrogate(text.charCodeAt(start))) start -= 1;
  if (end < text.length && isLowSurrogate(text.charCodeAt(end))) end += 1;

  const segments = [];
  if (start > 0) segments.push({ text: '…', highlight: false });
  let cursor = start;
  ranges.filter((range) => range.start < end).forEach((range) => {
    if (range.start > cursor) segments.push({ text: oneLine(text.slice(cursor, range.start)), highlight: false });
    segments.push({ text: oneLine(text.slice(range.start, range.end)), highlight: true });
    cursor = range.end;
  });
  if (cursor < end) segments.push({ text: oneLine(text.slice(cursor, end)), highlight: false });
  if (end < text.length) segments.push({ text: '…', highlight: false });
  return segments;
}
